/**
 * Roadmap Page (BoardV3)
 * Main page component for the roadmap view.
 *
 * Owns the boardLayoutStore + all resize hooks (header, sidebar, task-detail).
 * Wraps children in BoardLayoutProvider so that BoardV3, Toolbar, etc.
 * can read controls from context.
 */

import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector } from '@xstate/store/react';
import { BoardV3 } from '../components/BoardV3';
import { useBoardTaskDetailLevel } from '../hooks/useBoardTaskDetailLevel';
import { useBoardHeaderResize } from '../hooks/useBoardHeaderResize';
import { useBoardSidebarResize } from '../hooks/useBoardSidebarResize';
import { useBoardMultiSelect } from '../hooks/useBoardMultiSelect';
import { selectionStore } from '../stores/selectionStore';
import { popupStore } from '../stores/popupStore';
import {
  useAllTasks,
  useRoadmapActions,
  useColumnNames,
  useScheduledTasks,
  useBoardStartDate,
} from '../../stores/hooks';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { TaskModal } from '../../components/tasks';
import { useTaskModalState } from '../../hooks/useTaskModalState';
import { recalculateSchedule } from '../../stores';
import { ImportModal } from '../../components/Board/ImportModal';
import { TaskContextMenu } from '../components/menus/TaskContextMenu';
import { AssigneePickerPopover } from '../components/menus/AssigneePickerPopover';
import { BoardV3FloatingToolbar } from '../components/toolbar';
import { businessDayIndexToDate, getTaskDayIndex } from '../../lib/dateUtils';
import { getTaskDuration } from '../utils/boardHelpers';
import { BASE_DAY_WIDTH } from '../utils/boardHelpers';
import type { Task } from '../../types';
import type { TaskDetailLevel } from '../hooks/useBoardTaskDetailLevel';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

// Layout store
import { createBoardLayoutStore } from '../components/boardLayoutStore';
import { BoardLayoutProvider, type ViewControls } from '../components/BoardLayoutContext';

// Initial state helpers
import { getInitialSidebarState } from '../hooks/useBoardSidebarResize';
import { getInitialHeaderState } from '../hooks/useBoardHeaderResize';
import { getInitialDetailState } from '../hooks/useBoardTaskDetailLevel';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 20.0;

// Run an action and recalculate schedule afterwards (async to let store update first)
function scheduleAfter(action: () => void) {
  action();
  setTimeout(() => recalculateSchedule(), 0);
}

// ============================================
// Task context menu action handlers
// ============================================

function useTaskContextMenuHandlers(
  actions: {
    duplicateTask: (id: string) => void;
    removeFromBoard: (id: string) => void;
    deleteTask: (id: string) => void;
    splitTask: (id: string, dayOffset: number) => void;
  },
) {
  const taskContextMenu = useSelector(popupStore, s => s.context.taskContextMenu);

  const handleDuplicate = useCallback(() => {
    const menu = popupStore.getSnapshot().context.taskContextMenu;
    if (!menu) return;
    scheduleAfter(() => actions.duplicateTask(menu.taskId));
    popupStore.send({ type: 'popup.closeTaskMenu' });
  }, [actions.duplicateTask]);

  const handleRemoveFromBoard = useCallback(() => {
    const menu = popupStore.getSnapshot().context.taskContextMenu;
    if (!menu) return;
    scheduleAfter(() => actions.removeFromBoard(menu.taskId));
    popupStore.send({ type: 'popup.closeTaskMenu' });
  }, [actions.removeFromBoard]);

  const handleDelete = useCallback(() => {
    const menu = popupStore.getSnapshot().context.taskContextMenu;
    if (!menu) return;
    scheduleAfter(() => actions.deleteTask(menu.taskId));
    popupStore.send({ type: 'popup.closeTaskMenu' });
  }, [actions.deleteTask]);

  const handleOpenAssigneePicker = useCallback(() => {
    const menu = popupStore.getSnapshot().context.taskContextMenu;
    if (!menu) return;
    popupStore.send({ type: 'popup.openAssigneePicker', taskId: menu.taskId, x: menu.x, y: menu.y });
  }, []);

  const handleSplit = useCallback(() => {
    const menu = popupStore.getSnapshot().context.taskContextMenu;
    if (!menu) return;
    scheduleAfter(() => actions.splitTask(menu.taskId, menu.dayOffset));
    popupStore.send({ type: 'popup.closeTaskMenu' });
  }, [actions.splitTask]);

  return { taskContextMenu, handleDuplicate, handleRemoveFromBoard, handleDelete, handleOpenAssigneePicker, handleSplit };
}

// ============================================
// Popup subcomponents
// ============================================

function TaskContextMenuPopup({
  menuState,
  tasks,
  boardStartDate,
  handlers,
  taskDetail,
}: {
  menuState: { taskId: string; x: number; y: number; dayOffset: number };
  tasks: Task[];
  boardStartDate: string;
  handlers: ReturnType<typeof useTaskContextMenuHandlers>;
  taskDetail: { detailLevel: TaskDetailLevel; mode: 'auto' | 'manual'; setLevel: (l: TaskDetailLevel) => void; setAutoMode: () => void };
}) {
  const ctxTask = tasks.find(t => t.id === menuState.taskId);
  if (!ctxTask) return null;

  const totalDuration = getTaskDuration(ctxTask);
  const dayOffset = Math.max(1, Math.min(menuState.dayOffset, totalDuration - 1));
  const canSplit = totalDuration >= 2;
  const splitDate = canSplit
    ? businessDayIndexToDate(getTaskDayIndex(ctxTask.startDate, boardStartDate) + dayOffset, boardStartDate)
    : undefined;

  return (
    <TaskContextMenu
      x={menuState.x}
      y={menuState.y}
      task={ctxTask}
      splitDate={splitDate}
      onSplit={canSplit ? handlers.handleSplit : undefined}
      onAssign={handlers.handleOpenAssigneePicker}
      onDuplicate={handlers.handleDuplicate}
      onRemoveFromBoard={handlers.handleRemoveFromBoard}
      onDelete={handlers.handleDelete}
      onClose={() => popupStore.send({ type: 'popup.closeTaskMenu' })}
      detailLevel={taskDetail.detailLevel}
      detailMode={taskDetail.mode}
      onSetDetailLevel={taskDetail.setLevel}
      onSetDetailAutoMode={taskDetail.setAutoMode}
    />
  );
}

function AssigneePickerPopup({
  pickerState,
  tasks,
}: {
  pickerState: { taskId: string; x: number; y: number };
  tasks: Task[];
}) {
  const pickerTask = tasks.find(t => t.id === pickerState.taskId);
  if (!pickerTask) return null;

  return (
    <AssigneePickerPopover
      x={pickerState.x}
      y={pickerState.y}
      task={pickerTask}
      onClose={() => popupStore.send({ type: 'popup.closeAssigneePicker' })}
    />
  );
}

// ============================================
// Main Page Component
// ============================================

export default function BoardV3Page() {
  useDocumentTitle('Roadmap – qtplan');
  // Global store data
  const boardStartDate = useBoardStartDate();
  const tasks = useAllTasks();
  const columnNames = useColumnNames();
  const { deleteTask, removeFromBoard, addTask, duplicateTask, splitTask, updateColumn } = useRoadmapActions();
  const scheduledTasks = useScheduledTasks();

  // Local UI state
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [addTaskDefaults, setAddTaskDefaults] = useState<{ column?: string; startDate?: string }>({});

  const modal = useTaskModalState();

  // Grid config
  const dayWidth = BASE_DAY_WIDTH * zoomLevel;

  // ── Board Layout Store (created once, owned by this page) ──
  const layoutStore = useMemo(() => {
    const initialSidebar = getInitialSidebarState();
    const initialHeader = getInitialHeaderState(dayWidth);
    const initialDetail = getInitialDetailState();
    const { headerHeight: initHeaderHeight, ...headerRest } = initialHeader;
    const { rowHeight: initRowHeight, ...detailRest } = initialDetail;
    return createBoardLayoutStore({
      dayWidth,
      rowHeight: initRowHeight,
      headerHeight: initHeaderHeight,
      columnYOffsets: {},
      rowAssignments: {},
      tasksByColumn: {},
      totalHeight: 0,
      totalDays: 0,
      visibleWeeks: [],
      ...initialSidebar,
      ...headerRest,
      ...detailRest,
    });
  }, []);

  // ── Resize hooks (lifted from children, write into layoutStore) ──
  const taskDetail = useBoardTaskDetailLevel(layoutStore);
  const headerResize = useBoardHeaderResize(dayWidth, layoutStore);
  const sidebarResize = useBoardSidebarResize(layoutStore);

  // ── ViewControls object for context ──
  const viewControls = useMemo((): ViewControls => ({
    header: headerResize,
    sidebar: sidebarResize,
    taskDetail,
  }), [headerResize, sidebarResize, taskDetail]);

  // Multi-select (remove/delete handlers now in toolbar, but multiSelect hook still needed for detail panel)
  const handleRemoveSelectedFromBoard = useCallback(() => {
    const ids = selectionStore.getSnapshot().context.selectedIds;
    for (const id of ids) {
      removeFromBoard(id);
    }
    selectionStore.send({ type: 'selection.clear' });
    setTimeout(() => recalculateSchedule(), 0);
  }, [removeFromBoard]);

  // Hook needed for keyboard listeners (Delete/Escape) + selection pruning
  useBoardMultiSelect({ tasks, onRemoveSelected: handleRemoveSelectedFromBoard });

  // Popup state from popupStore
  const assigneePickerState = useSelector(popupStore, s => s.context.assigneePicker);
  const addTaskRequest = useSelector(popupStore, s => s.context.addTaskRequest);
  const editTaskRequest = useSelector(popupStore, s => s.context.editTaskRequest);

  // React to addTaskRequest from context menus (dispatched via popupStore)
  useEffect(() => {
    if (addTaskRequest) {
      setAddTaskDefaults({ column: addTaskRequest.column, startDate: addTaskRequest.date });
      modal.openCreate();
      popupStore.send({ type: 'popup.clearAddTaskRequest' });
    }
  }, [addTaskRequest, modal.openCreate]);

  // React to editTaskRequest (double-click on task)
  useEffect(() => {
    if (editTaskRequest) {
      modal.openEdit(editTaskRequest.taskId, tasks.map(t => t.id));
      popupStore.send({ type: 'popup.clearEditTaskRequest' });
    }
  }, [editTaskRequest, tasks, modal.openEdit]);

  // Context menu handlers
  const ctxHandlers = useTaskContextMenuHandlers(
    { duplicateTask, removeFromBoard, deleteTask, splitTask },
  );

  // Zoom handler (for board's pinch-to-zoom)
  const handleSetZoom = useCallback((level: number) => {
    setZoomLevel(Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, level)));
  }, []);

  const handleOpenAddTaskModal = useCallback(() => {
    setAddTaskDefaults({});
    modal.openCreate();
  }, [modal.openCreate]);

  const handleAddTask = useCallback((task: Parameters<typeof addTask>[0]) => {
    scheduleAfter(() => addTask(task));
  }, [addTask]);

  const handleCloseTaskModal = useCallback(() => {
    modal.close();
    setAddTaskDefaults({});
  }, [modal.close]);

  return (
    <BoardLayoutProvider store={layoutStore} viewControls={viewControls}>
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <BoardV3
            zoomLevel={zoomLevel}
            onZoomChange={handleSetZoom}
          />
        </div>

        <BoardV3FloatingToolbar
          onAddTask={handleOpenAddTaskModal}
        />

        <ImportModal />

        {/* Task Context Menu */}
        {ctxHandlers.taskContextMenu && (
          <TaskContextMenuPopup
            menuState={ctxHandlers.taskContextMenu}
            tasks={tasks}
            boardStartDate={boardStartDate}
            handlers={ctxHandlers}
            taskDetail={taskDetail}
          />
        )}

        {/* Assignee Picker Popover */}
        {assigneePickerState && (
          <AssigneePickerPopup
            pickerState={assigneePickerState}
            tasks={tasks}
          />
        )}

        <TaskModal
          isOpen={modal.isOpen}
          onClose={handleCloseTaskModal}
          mode={modal.mode}
          taskId={modal.taskId}
          taskHistory={modal.taskHistory}
          historyIndex={modal.historyIndex}
          onNavigate={modal.navigate}
          onAdd={handleAddTask}
          onUpdate={(task) => {
            globalTasksStore.send({ type: 'task.updateTitle', taskId: task.id, title: task.title });
            globalTasksStore.send({ type: 'task.updateStartDate', taskId: task.id, startDate: task.startDate });
            globalTasksStore.send({ type: 'task.updateCategories', taskId: task.id, categoryIds: task.categoryIds ?? [] });
            globalTasksStore.send({ type: 'task.updateDescription', taskId: task.id, description: Array.isArray(task.description) ? task.description : undefined });
            globalTasksStore.send({ type: 'task.updateConsequence', taskId: task.id, consequence: Array.isArray(task.consequence) ? task.consequence : undefined });
            globalTasksStore.send({ type: 'task.updateRequiredSkills', taskId: task.id, requiredSkills: typeof task.requiredSkills === 'string' ? task.requiredSkills : undefined });
            globalTasksStore.send({ type: 'task.updateEstimateMode', taskId: task.id, estimateMode: task.estimateMode });
            globalTasksStore.send({ type: 'task.updateEstimates', taskId: task.id, defaultEstimate: task.defaultEstimate, estimates: task.estimates });
            if (task.column) {
              updateColumn(task.id, task.column);
            }
            recalculateSchedule();
          }}
          getTaskById={(tId) => tasks.find(t => t.id === tId)}
          getScheduledTask={(tId) => scheduledTasks.get(tId)}
          columns={columnNames}
          defaultColumn={addTaskDefaults.column}
          defaultStartDate={addTaskDefaults.startDate}
          onDelete={(taskId) => { scheduleAfter(() => deleteTask(taskId)); }}
          onRemoveFromBoard={(taskId) => { scheduleAfter(() => removeFromBoard(taskId)); }}
        />
      </div>
    </BoardLayoutProvider>
  );
}
